
import { useEffect } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SwipeableTabs } from "@/components/ui/swipeable-tabs";
import { useState } from "react";
import { useIsAdmin } from "@/hooks/useAnalytics";
import UserRoleManager from "@/components/admin/UserRoleManager";
import UserActivityTracker from "@/components/admin/UserActivityTracker";
import PageAnalytics from "@/components/admin/PageAnalytics";
import { Loader2, Users, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { data: isAdmin, isLoading } = useIsAdmin();
  
  // Redirect non-admin users
  useEffect(() => {
    if (!isLoading && isAdmin === false) {
      navigate("/");
    }
  }, [isAdmin, isLoading, navigate]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  // Only render dashboard if user is admin
  if (!isAdmin) {
    return null;
  }
  
  return (
    <div className="bg-background w-full">
      <div className="w-full px-6 py-6 space-y-6">
      <PageHeader title="Admin Dashboard" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              Manage console users, roles and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Comprehensive role-based access control system with granular permissions for every
              aspect of the product.
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/admin/users-and-roles")}>
              <Users className="mr-2 h-4 w-4" />
              Manage Users & Roles
            </Button>
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Usage Analytics</CardTitle>
            <CardDescription>
              View detailed analytics about system usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Insights into user activity, page visits, and system usage patterns.
            </p>
          </CardContent>
          <CardFooter>
            <Button variant="outline" onClick={() => document.getElementById("analytics-tab")?.click()}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              View Analytics
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      <AdminTabs />
      </div>
    </div>
  );
}

function AdminTabs() {
  const [tab, setTab] = useState("users");
  return (
    <SwipeableTabs
      value={tab}
      onValueChange={setTab}
      paneClassName="pt-6 px-0"
      items={[
        { value: "users", label: "User Management", content: <UserRoleManager /> },
        { value: "activity", label: "User Activity", content: <UserActivityTracker /> },
        { value: "analytics", label: "Analytics", content: <PageAnalytics /> },
      ]}
    />
  );
}
